import React, { createContext, useState, useEffect } from "react";
import { authAdminService } from "../Services/authService";
import { platformService } from "../Services/platformService";
import { clientAdminService } from "../Services/clientAdminService";
import { contractAdminService } from "../Services/contractAdminService";
import { withdrawAdminService } from "../Services/withdrawAdminService";
import { ADMIN_BLOQUEADO, limparSessaoAdmin } from "../systemStatus";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    isAuthenticated: false,
    accessToken: null,
    permissions: "",
    viewPermissions: [],
    editPermissions: [],
  });
  const [clientsDataBase, setClientsDatabase] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPurchasesAtPlatform, setTotalPurchasesAtPlatform] = useState(0);
  const [totalClientsWithMoney, setTotalClientsWithMoney] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [newUserBeingCreated, setNewUserBeingCreated] = useState(false);
  const [totalPendingPurchases, setTotalPendingPurchases] = useState(false);
  const [pendingPurchases, setPendingPurchases] = useState(false);

  useEffect(() => {
    const restoreSession = async () => {
      // Sistema bloqueado: descarta qualquer sessao salva e mantem o usuario no login.
      if (ADMIN_BLOQUEADO) {
        limparSessaoAdmin();
        return;
      }

      const token = localStorage.getItem("adminAccessToken");
      const email = localStorage.getItem("admin-email-golden");
      if (token && email) {
        setAuthState((prevState) => ({ ...prevState, accessToken: token }));
        try {
          const adminData = await authAdminService.getAdminByEmail(email);
          if (adminData) {
            setAuthState({
              user: { email: adminData.email },
              isAuthenticated: true,
              accessToken: token,
              permission: adminData.permissions || "",
              viewPermissions: adminData.viewPermissions || [],
              editPermissions: adminData.editPermissions || [],
            });
            await getPlatformInfo();
          } else {
            logout();
          }
        } catch (error) {
          logout();
        }
      }
    };
    if (!newUserBeingCreated) {
      restoreSession();
    }
  }, [newUserBeingCreated]);

  const login = async (email, password, ipaddress) => {
    try {
      const { accessToken, refreshToken } = await authAdminService.login(
        email,
        password,
        ipaddress
      );

      localStorage.setItem("adminAccessToken", accessToken);
      localStorage.setItem("adminRefreshToken", refreshToken);
      localStorage.setItem("admin-email-golden", email);

      const adminData = await authAdminService.getAdminByEmail(email);

      if (adminData) {
        setAuthState({
          user: { email: adminData.email },
          isAuthenticated: true,
          accessToken: accessToken,
          permission: adminData.permissions || "",
          viewPermissions: adminData.viewPermissions || [],
          editPermissions: adminData.editPermissions || [],
        });
        await getPlatformInfo();
      } else {
        throw new Error("Dados do administrador não encontrados após o login.");
      }
    } catch (error) {
      console.error("Erro no processo de login:", error);
      throw new Error("E-mail ou senha inválidos.");
    }
  };

  const logout = () => {
    localStorage.removeItem("adminAccessToken");
    localStorage.removeItem("adminRefreshToken");
    localStorage.removeItem("admin-email-golden");
    setAuthState({
      user: null,
      isAuthenticated: false,
      accessToken: null,
      permission: "",
    });
    window.location.href = "/";
  };

  const getPlatformInfo = async () => {
    try {
      const data = await platformService.getPlatformInfo();
      if (data) {
        setTotalClients(data.totalClients);
        setTotalClientsWithMoney(data.clientsWithContracts);
        setTotalPurchasesAtPlatform(
          data.activeContracts +
            data.canceledContracts +
            data.clientsWithContracts +
            data.endedContracts
        );
        setTotalPendingPurchases(data.pendingContracts);
        setTotalWithdrawals(data.totalWithdraws);
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === 401) logout();
    }
  };

  const getClients = async (limit, offset, searchTerm) => {
    try {
      const data = await clientAdminService.searchClients(
        limit,
        offset,
        searchTerm
      );
      setClientsDatabase(data);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401 || err.response?.status === 403)
        logout();
    }
  };

  const getPurchases = async (
    limit,
    offset,
    order,
    status,
    searchTerm,
    idPurchase,
    promotionSearch,
    isRepurchase
  ) => {
    try {
      const data = await contractAdminService.searchContracts(
        limit,
        offset,
        order,
        status,
        searchTerm,
        idPurchase,
        promotionSearch,
        isRepurchase
      );
      setPurchases(data.contracts);
      setTotalPurchases(data.totalContracts);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401 || err.response?.status === 403)
        logout();
    }
  };

  const getPendingPurchases = async (
    page,
    limit = 10,
    order = "date_desc",
    searchTerm,
    isRepurchase
  ) => {
    try {
      const data = await contractAdminService.getPendingContracts(
        page,
        limit,
        order,
        searchTerm,
        isRepurchase
      );
      setPendingPurchases(data.contracts);
      setTotalPendingPurchases(data.totalContracts);
      return data.contracts;
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401 || err.response?.status === 403)
        logout();
    }
  };

  const getSaques = async (
    limit,
    offset,
    order,
    status,
    searchTerm,
    startDate,
    endDate,
    contractId // Recebe da tela
  ) => {
    try {
      const data = await withdrawAdminService.searchWithdraws(
        limit,
        offset,
        order,
        status,
        searchTerm,
        startDate,
        endDate,
        contractId // Repassa para o serviço
      );
      setWithdrawals(data.withdraws || []);
      setTotalWithdrawals(data.totalCount || 0);
    } catch (err) {
      console.error("Erro ao buscar saques:", err);
      if (err.response?.status === 401 || err.response?.status === 403)
        logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        logout,
        getClients,
        clientsDataBase,
        totalClients,
        totalClientsWithMoney,
        getPurchases,
        purchases,
        totalPurchases,
        withdrawals,
        totalWithdrawals,
        getSaques,
        totalPurchasesAtPlatform,
        newUserBeingCreated,
        setNewUserBeingCreated,
        createClient: clientAdminService.createClient,
        getPendingPurchases,
        totalPendingPurchases,
        pendingPurchases,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
